import axios from "axios";
import { actAsHeader } from "@/lib/actAs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  const id = cfg.noActAs ? null : actAsHeader();
  if (id && !cfg.headers["X-Act-As"]) cfg.headers["X-Act-As"] = id;
  return cfg;
}, null, { synchronous: true });

// Silent session renewal: on 401, refresh the access cookie once and replay the request
let refreshing = null;
// Unstable connection: replay safe requests (GET/PUT and idempotent quote saves) up to 3 times
const RETRY_DELAYS = [1000, 3000, 6000];
const retriable = (cfg) => {
  const m = (cfg.method || "get").toLowerCase();
  return m === "get" || m === "put" || (m === "post" && cfg.url === "/quotes" && /"draft_id":"[0-9a-f-]{36}"/.test(typeof cfg.data === "string" ? cfg.data : JSON.stringify(cfg.data || {})));
};
const netFail = (error) => !error.response || [502, 503, 504].includes(error.response.status);
let netDown = false;
export const setNetState = (down) => {
  if (netDown && !down) window.dispatchEvent(new Event("net:back"));
  if (netDown !== down) { netDown = down; window.dispatchEvent(new CustomEvent("net:state", { detail: { down } })); }
};
if (typeof window !== "undefined") window.addEventListener("online", () => { if (netDown) { netDown = false; window.dispatchEvent(new Event("net:back")); } });
api.interceptors.response.use(
  (r) => { setNetState(r.headers?.["x-sw-cache"] === "1"); return r; },
  async (error) => {
    const cfg = error.config;
    if (!cfg || error.code === "ERR_CANCELED" || !netFail(error)) throw error;
    setNetState(true);
    const n = cfg._netTry || 0;
    if (n >= RETRY_DELAYS.length || !retriable(cfg) || navigator.onLine === false) throw error;
    cfg._netTry = n + 1;
    await new Promise((res) => { setTimeout(res, RETRY_DELAYS[n]); });
    return api(cfg);
  },
);
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    const url = cfg?.url || "";
    if (error.response?.status !== 401 || !cfg || cfg._retried || /\/auth\/(login|refresh|logout|forgot-password|reset-password)/.test(url)) {
      throw error;
    }
    cfg._retried = true;
    refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
    try { await refreshing; } catch { window.dispatchEvent(new Event("auth:expired")); return new Promise(() => {}); }
    return api(cfg);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
